/** @forge-header
 * @id          soccerParser
 * @role        Soccer ticker parser | KXUECLGAME / KXWCGAME / KXINTLFRIENDLYGAME 3-way game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitAnchoredCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseSoccerGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitAnchoredCompetitors, isDrawOutcome } from '../helpers';

/**
 * Soccer: KXUECLGAME-26MAY27CRYRVC-CRY | KXWCGAME-26JUN27JORARG-TIE
 * | KXINTLFRIENDLYGAME-26JUN08FRAIRL-FRA
 * 3-way (Home / Away / Draw) — one ticker per outcome. The marketStore
 * multi-outcome builder groups the three tickers by event; this lib parser
 * exists for display (TickerDisplay chip + describeMarket label on positions).
 *
 * PACK ORDER IS NOT AN ORIENTATION (#1445). Kalshi lists the HOME team first
 * in soccer tickers, but `game.awayCode`/`homeCode` here are filled by pack
 * position (first/second) because that is the shape every consumer shares.
 * Which side is home is the producer's call (`canonicalAwayCode`/`HomeCode`,
 * `canonicalTeams`): the store, describeMarket and the board read that stamp
 * and use this pair only when no stamp exists. Do not "fix" the order here.
 *
 * The Draw ticker's outcome bounds neither team, so it leaves the matchup
 * UNRESOLVED (#837) — the label falls back to the exact ticker plus "Draw"
 * rather than to a midpoint guess that disagrees with the two sibling markets
 * of the same event. describeMarket recovers the pair from hydra's canonical
 * away/home when a frame is in hand, which for soccer it reliably is.
 */
export function parseSoccerGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const series = parts[0] ?? 'KXSOCCER';
  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;

  const pair = splitAnchoredCompetitors(mid.slice(7), outcome);
  const game: GameInfo | null = pair ? { awayCode: pair[0], homeCode: pair[1] } : null;

  const outcomeLabel = isDrawOutcome(outcome) ? 'Draw' : outcome ? `${outcome} Win` : null;
  const matchup = game ? `${game.awayCode} v ${game.homeCode}` : raw;
  const shortLabel = `Soccer: ${matchup}`;
  const fullLabel = `Soccer: ${matchup}${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'soccer-game',
    series,
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer knockout "To Advance": KXWCADVANCE-26JUL01ENGCOD-ENG |
 * KXUECLADVANCE-…. A 2-way (the two teams, NO draw) settled on who advances
 * incl. extra time + penalties. One ticker per team — the outcome is always a
 * team code (never TIE), so it splits the team chunk like a moneyline — and
 * leaves the matchup unresolved on the (unobserved) shape that carries no
 * outcome at all, rather than midpoint-guessing it (#837).
 * Reuses the 'soccer-game' category (no new color-map entry); the
 * 'KXSOCCERADVANCE' series marker lets describeMarket tag the bet team as
 * "<TEAM> Adv" so a held position reads apart from the regulation 1X2 winner.
 */
export function parseSoccerAdvance(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null; // a team code (never a draw)

  const pair = splitAnchoredCompetitors(mid.slice(7), outcome);
  const game: GameInfo | null = pair ? { awayCode: pair[0], homeCode: pair[1] } : null;

  const advLabel = outcome ? `${outcome} Advances` : 'To Advance';
  const matchup = game ? `${game.awayCode} v ${game.homeCode}` : raw;
  const shortLabel = `Soccer: ${matchup} · To Advance`;
  const fullLabel = `Soccer: ${matchup} · ${advLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERADVANCE',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer BTTS (both teams to score): KXWCBTTS-26JUN18SUIBIH-BTTS |
 * KXWC1HBTTS-… | KXWC2HBTTS-…. A single binary per game/period — no full-game
 * spread/total/game equivalent, so it parses directly (parse.ts dispatches the
 * BTTS prefix here). Outcome is always 'BTTS'; the period (if any) annotates the
 * side label. BTTS names no competitor at all — there has never been an
 * outcome here to bound the packed pair — so the matchup is left UNRESOLVED
 * and the label carries the exact ticker plus "Both Teams To Score" (#837).
 * describeMarket restores the matchup from hydra's canonical away/home, which
 * is stamped per fixture and so covers BTTS like every other family.
 * Reuses the 'soccer-game' category to avoid a new color-map entry.
 */
export function parseSoccerBtts(
  raw: string,
  rest: string,
  period?: ParsedTicker['period'],
): ParsedTicker {
  const mid = (rest.split('-')[0] ?? '');
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));

  const periodLabel = period === '1H' ? ' · 1st Half' : period === '2H' ? ' · 2nd Half' : '';
  const shortLabel = `Soccer: ${raw} · BTTS`;
  const fullLabel = `Soccer: ${raw} · Both Teams To Score${periodLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERBTTS',
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: 'BTTS',
    period,
    shortLabel,
    fullLabel,
  };
}
